import React, { useMemo } from 'react';
import { Diamond } from 'lucide-react';

const ProgramTimeline = ({ data, onProgramClick, loading }) => {
  // Group programs by OEM
  const groupedByOem = useMemo(() => {
    if (!data || data.length === 0) return {};
    
    const groups = {};
    data.forEach(program => {
      if (!groups[program.oem]) {
        groups[program.oem] = [];
      }
      groups[program.oem].push(program);
    });
    
    // Sort programs within each OEM by SOP date
    Object.keys(groups).forEach(oem => {
      groups[oem].sort((a, b) => {
        const dateA = new Date(a.sop_date);
        const dateB = new Date(b.sop_date);
        return dateA - dateB;
      });
    });
    
    return groups;
  }, [data]);

  // Calculate timeline range
  const timelineRange = useMemo(() => {
    if (!data || data.length === 0) {
      const currentYear = new Date().getFullYear();
      return {
        start: new Date(currentYear - 1, 0, 1),
        end: new Date(currentYear + 3, 11, 31),
      };
    }

    const dates = data.map(p => new Date(p.sop_date || p.contract_start_date)).filter(d => !isNaN(d));
    if (dates.length === 0) {
      const currentYear = new Date().getFullYear();
      return {
        start: new Date(currentYear - 1, 0, 1),
        end: new Date(currentYear + 3, 11, 31),
      };
    }

    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));
    
    return {
      start: new Date(minDate.getFullYear(), 0, 1),
      end: new Date(maxDate.getFullYear() + 1, 11, 31),
    };
  }, [data]);

  // Generate quarters for timeline
  const quarters = useMemo(() => {
    const result = [];
    const start = timelineRange.start;
    const end = timelineRange.end;
    
    let current = new Date(start);
    while (current <= end) {
      const year = current.getFullYear();
      const quarter = Math.floor(current.getMonth() / 3) + 1;
      result.push({ year, quarter, date: new Date(current) });
      current.setMonth(current.getMonth() + 3);
    }
    
    return result;
  }, [timelineRange]);

  const getRiskColor = (riskLevel) => {
    switch (riskLevel) {
      case 'high':
        return 'bg-red-500';
      case 'medium':
        return 'bg-yellow-500';
      case 'low':
      default:
        return 'bg-green-500';
    }
  };

  const calculatePosition = (date) => {
    if (!date) return 0;
    const d = new Date(date);
    const totalDuration = timelineRange.end - timelineRange.start;
    const position = (d - timelineRange.start) / totalDuration;
    return Math.max(0, Math.min(100, position * 100));
  };

  const calculateWidth = (startDate, endDate) => {
    if (!startDate || !endDate) return 10; // Default width for programs without end date
    const start = new Date(startDate);
    const end = new Date(endDate);
    const totalDuration = timelineRange.end - timelineRange.start;
    const programDuration = end - start;
    return Math.max(5, Math.min(95, (programDuration / totalDuration) * 100));
  };

  const formatCurrency = (cents) => {
    if (!cents) return '$0';
    const dollars = cents / 100;
    if (dollars >= 1000000) {
      return `$${(dollars / 1000000).toFixed(0)}M`;
    }
    return `$${(dollars / 1000).toFixed(0)}K`;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-32 bg-gray-200 rounded animate-pulse"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No programs found for the selected filters.</p>
        <p className="text-sm mt-2">Try adjusting your OEM or date range filters.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Timeline Header */}
      <div className="relative overflow-x-auto">
        <div className="min-w-max">
          {/* Quarter Labels */}
          <div className="flex border-b border-gray-300 pb-2 mb-4">
            {quarters.map((q, idx) => (
              <div
                key={idx}
                className="flex-1 min-w-[100px] text-center text-xs font-medium text-gray-600"
              >
                Q{q.quarter} {q.year}
              </div>
            ))}
          </div>

          {/* Program Bars by OEM */}
          <div className="space-y-8">
            {Object.entries(groupedByOem).map(([oem, programs]) => (
              <div key={oem} className="relative">
                {/* OEM Label */}
                <div className="flex items-center mb-3">
                  <div className="text-sm font-semibold text-gray-700 w-32">{oem}</div>
                  <div className="flex-1 h-px bg-gray-200"></div>
                </div>

                {/* Programs for this OEM */}
                <div className="relative space-y-3 ml-32">
                  {programs.map((program, idx) => {
                    const startPos = calculatePosition(program.contract_start_date || program.sop_date);
                    const width = calculateWidth(program.contract_start_date, program.contract_end_date);
                    const sopPos = calculatePosition(program.sop_date);

                    return (
                      <div key={program.opp_id} className="relative h-8">
                        {/* Program Bar */}
                        <div
                          className="absolute top-0 h-full"
                          style={{
                            left: `${startPos}%`,
                            width: `${width}%`,
                          }}
                        >
                          <div
                            className={`h-full rounded ${getRiskColor(program.risk_level)} opacity-70 hover:opacity-90 cursor-pointer transition-all`}
                            onClick={() => onProgramClick(program.opp_id)}
                            title={`${program.program_name} - ${formatCurrency(program.contract_value)}`}
                          >
                            <div className="px-2 py-1 text-xs text-white truncate font-medium">
                              {program.program_name}
                            </div>
                          </div>
                        </div>

                        {/* SOP Diamond Marker */}
                        {program.sop_date && (
                          <div
                            className="absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2"
                            style={{ left: `${sopPos}%` }}
                          >
                            <Diamond className="w-4 h-4 text-gray-900 fill-white" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center space-x-6 pt-4 border-t border-gray-200">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-green-500 rounded"></div>
          <span className="text-xs text-gray-600">Low Risk (&lt;5%)</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-yellow-500 rounded"></div>
          <span className="text-xs text-gray-600">Medium Risk (5-10%)</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-red-500 rounded"></div>
          <span className="text-xs text-gray-600">High Risk (&gt;10%)</span>
        </div>
        <div className="flex items-center space-x-2">
          <Diamond className="w-4 h-4 text-gray-900 fill-white" />
          <span className="text-xs text-gray-600">SOP Date</span>
        </div>
      </div>
    </div>
  );
};

export default ProgramTimeline;
